import { useCollaborations } from '@/hooks/useCollaborations'
import { useState, useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { toast } from '@/components/ui/sonner'
import {
  BanknoteIcon,
  Check,
  Link,
  User,
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { calculateEarnings } from '@/utils'
import athleteApi from '@/lib/api'

interface PendingInvoice {
  id: string
  collaboration_id: string
  invoice_number: string
  status: 'sent_to_athlete' | 'sent_to_brand' | 'paid' | 'draft' | 'void'
  total_cents: number
  currency: string
  sent_to_athlete_at: string | null
  sent_to_brand_at: string | null
}

type StripeStatus = {
  connected: boolean
  status: 'not_connected' | 'pending' | 'restricted' | 'complete'
  details_submitted?: boolean
  payouts_enabled?: boolean
  charges_enabled?: boolean
  created_at?: string
}

const Payments = () => {
  const { collaborations, refetch: refreshCollaborations } = useCollaborations()
  const { athlete } = useAuth()
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [stripeStatus, setStripeStatus] = useState<StripeStatus | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [invoices, setInvoices] = useState<PendingInvoice[]>([])

  // Fetch Stripe connection status on mount
  useEffect(() => {
    const fetchStripeStatus = async () => {
      try {
        const response = await athleteApi.get('/athlete/stripe/status')
        setStripeStatus(response.data)
      } catch (error) {
        console.error('Error fetching Stripe status:', error)
        setStripeStatus({ connected: false, status: 'not_connected' })
      } finally {
        setLoadingStatus(false)
      }
    }

    fetchStripeStatus()
  }, [])

  // Load invoices visible to this athlete (review queue + sent + paid).
  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const res = await athleteApi.get('/athlete/invoices')
        setInvoices(res.data?.invoices ?? [])
      } catch (error) {
        console.error('Error fetching invoices:', error)
      }
    }
    fetchInvoices()
  }, [])

  // Calculate total earnings and pending payments
  const completedCollaborations = collaborations.filter(
    (collaboration) =>
      collaboration.paymentStatus === 'paid' || collaboration.paymentStatus === 'pending',
  )

  const { totalEarnings, pendingPayments, paidPayments } = calculateEarnings(collaborations)

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      // Whole dollars like the Deals page; cents only when there are some
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const handleFixStripeAccount = async () => {
    setSubmitting(true)
    try {
      const response = await athleteApi.post('/athlete/stripe/refresh-link')
      window.location.href = response.data.url
    } catch (error) {
      console.error('Error generating Stripe update link:', error)
      toast.error('Error', { description: 'Failed to open Stripe. Please try again.' })
      setSubmitting(false)
    }
  }

  const handleConnectBank = async () => {
    setSubmitting(true)

    try {
      const response = await athleteApi.post('/athlete/stripe/connect')
      const { url } = response.data

      // Redirect to Stripe-hosted onboarding
      window.location.href = url
    } catch (error: unknown) {
      console.error('Error connecting to Stripe:', error)

      // Check if already connected
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { status?: string } } }
        if (axiosError.response?.data?.status === 'complete') {
          toast('Already Connected', {
            description: 'Your account is already connected and enabled for payouts.',
          })
          setSubmitting(false)
          return
        }
      }

      toast.error('Connection Error', {
        description: 'Failed to connect to Stripe. Please try again.',
      })
      setSubmitting(false)
    }
  }

  const chip =
    'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold'
  const getConnectionStatusBadge = () => {
    if (loadingStatus) {
      return <span className={`${chip} bg-muted text-muted-foreground`}>Loading…</span>
    }
    if (!stripeStatus?.connected) {
      return <span className={`${chip} bg-[#FEE2E2] text-[#B91C1C]`}>Not connected</span>
    }
    if (stripeStatus.status === 'complete') {
      return (
        <span className={`${chip} bg-[#E8F9D2] text-[#2F5E06]`}>
          <CheckCircle className="h-3 w-3" /> Connected
        </span>
      )
    }
    if (stripeStatus.status === 'restricted') {
      return (
        <span className={`${chip} bg-[#FEE2E2] text-[#B91C1C]`}>
          <AlertTriangle className="h-3 w-3" /> Action required
        </span>
      )
    }
    return (
      <span className={`${chip} bg-[#FEF3C7] text-[#92400E]`}>
        <Clock className="h-3 w-3" /> Pending verification
      </span>
    )
  }

  const card = 'rounded-2xl border border-border bg-card p-4 shadow-sm md:p-6'
  const sectionLabel = 'mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground'
  const notice = 'rounded-2xl p-4 text-sm'

  const connectButton = (label: string) => (
    <button
      onClick={handleConnectBank}
      disabled={submitting}
      className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#C0FD72] px-6 py-3 text-sm font-semibold text-[#0F172A] hover:bg-[#d2ff9c] disabled:opacity-50 md:w-auto"
    >
      {submitting ? (
        <>
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#0F172A] border-t-transparent" />
          Connecting…
        </>
      ) : (
        <>
          <ExternalLink className="h-4 w-4" />
          {label}
        </>
      )}
    </button>
  )

  return (
    <div className="max-w-full pb-4">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl font-bold text-foreground">Earnings</h1>
        <p className="text-sm text-muted-foreground">
          What you've earned, what's on its way, and where it gets paid.
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full" onValueChange={setActiveTab}>
        <TabsList className="mb-4 grid w-full grid-cols-2 md:w-auto md:inline-grid">
          <TabsTrigger
            value="overview"
            className="data-[state=active]:bg-[#C0FD72] data-[state=active]:text-[#0F172A]"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="setup"
            className="data-[state=active]:bg-[#C0FD72] data-[state=active]:text-[#0F172A]"
          >
            Payment setup
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <PendingInvoicesPanel invoices={invoices} />

          <section>
            <h2 className={sectionLabel}>Summary</h2>
            <div className="grid grid-cols-3 gap-2 md:gap-4">
              {[
                { label: 'Total', value: totalEarnings, tone: 'text-foreground' },
                { label: 'Paid', value: paidPayments, tone: 'text-[#2F5E06]' },
                { label: 'Pending', value: pendingPayments, tone: 'text-[#92400E]' },
              ].map((s) => (
                <div
                  key={s.label}
                  className="min-w-0 rounded-2xl border border-border bg-card p-3 shadow-sm md:p-4"
                >
                  <p className="text-xs font-semibold text-muted-foreground">{s.label}</p>
                  <p className={`truncate text-lg font-bold tabular-nums md:text-2xl ${s.tone}`}>
                    {formatCurrency(s.value)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <section className="lg:col-span-2">
              <h2 className={sectionLabel}>Payment history · {completedCollaborations.length}</h2>
              {totalEarnings > 0 && completedCollaborations.length > 0 ? (
                <ul className="space-y-2">
                  {completedCollaborations.map((collaboration) => (
                    <li
                      key={collaboration.id}
                      className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm"
                    >
                      <img
                        src={collaboration.brand.logo}
                        alt={collaboration.brand.name}
                        className="h-10 w-10 shrink-0 rounded-xl object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-foreground">
                          {collaboration.title}
                        </p>
                        <p className="truncate text-sm text-muted-foreground">
                          {collaboration.brand.name}
                          {collaboration.paymentDate
                            ? ` · ${formatDate(collaboration.paymentDate)}`
                            : ''}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-semibold tabular-nums text-foreground">
                          {formatCurrency(Number(collaboration.paymentAmount))}
                        </p>
                        {collaboration.paymentStatus === 'paid' ? (
                          <span className={`${chip} bg-[#E8F9D2] text-[#2F5E06]`}>Paid</span>
                        ) : (
                          <span className={`${chip} bg-[#FEF3C7] text-[#92400E]`}>Pending</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                  No payments yet. They show up here once a deal is done.
                </p>
              )}
            </section>

            <section>
              <h2 className={sectionLabel}>Payout account</h2>
              <div className={`${card} space-y-3 lg:sticky lg:top-6`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-foreground">Connected account</p>
                  {getConnectionStatusBadge()}
                </div>

                {stripeStatus?.connected && stripeStatus.status === 'complete' ? (
                  <div className={`${notice} bg-[#E8F9D2] text-[#2F5E06]`}>
                    <p className="flex items-center gap-2 font-semibold">
                      <CheckCircle className="h-4 w-4" /> Bank account connected
                    </p>
                    <p>
                      Connected via Stripe
                      {stripeStatus.created_at
                        ? ` since ${formatDate(stripeStatus.created_at)}`
                        : ''}
                    </p>
                  </div>
                ) : stripeStatus?.connected && stripeStatus.status === 'restricted' ? (
                  <div className={`${notice} space-y-3 bg-[#FEE2E2] text-[#B91C1C]`}>
                    <p className="flex items-center gap-2 font-semibold">
                      <AlertTriangle className="h-4 w-4" /> Action required
                    </p>
                    <p>Stripe needs more information before it can turn on your payouts.</p>
                    <button
                      onClick={handleFixStripeAccount}
                      disabled={submitting}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#B91C1C] px-4 py-2 text-sm font-semibold text-[#FFFFFF] hover:bg-[#991B1B] disabled:opacity-50"
                    >
                      <ExternalLink className="h-4 w-4" />
                      {submitting ? 'Opening Stripe…' : 'Resolve in Stripe'}
                    </button>
                  </div>
                ) : stripeStatus?.connected && stripeStatus.status === 'pending' ? (
                  <div className={`${notice} bg-[#FEF3C7] text-[#92400E]`}>
                    <p className="flex items-center gap-2 font-semibold">
                      <Clock className="h-4 w-4" /> Verification pending
                    </p>
                    <p>Stripe is checking your account. This usually takes 1–2 business days.</p>
                  </div>
                ) : (
                  <div className={`${notice} bg-[#FEF3C7] text-[#92400E]`}>
                    <p className="font-semibold">No bank account yet</p>
                    <p>Connect one in Payment setup so brands can pay you.</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </TabsContent>

        <TabsContent value="setup" className="space-y-6">
          <div className={`${card} space-y-5`}>
            <div>
              <h2 className="text-xl font-bold text-foreground">Get paid fast and direct</h2>
              <p className="text-sm text-muted-foreground">
                Connect your bank account securely with Stripe. No fees, no hassle.
              </p>
            </div>

            <ol className="grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-4">
              {[
                { icon: User, title: 'Join a campaign', body: 'Accept brand deals that fit you' },
                { icon: Link, title: 'Connect your bank', body: 'Link your account with Stripe' },
                {
                  icon: BanknoteIcon,
                  title: 'Get paid directly',
                  body: 'Earnings go straight to your bank',
                },
              ].map((step, i) => (
                <li
                  key={step.title}
                  className="flex items-center gap-3 rounded-2xl border border-border p-3"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E8F9D2] text-[#2F5E06]">
                    <step.icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-foreground">
                      {i + 1}. {step.title}
                    </span>
                    <span className="block text-sm text-muted-foreground">{step.body}</span>
                  </span>
                </li>
              ))}
            </ol>

            <ul className="space-y-2">
              {['Secure bank connection', 'No middleman fees', 'Fast processing'].map((t) => (
                <li
                  key={t}
                  className="flex items-center gap-3 text-sm font-semibold text-foreground"
                >
                  <span className="rounded-full bg-[#C0FD72] p-1">
                    <Check className="h-3.5 w-3.5 text-[#0F172A]" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>

            <p className={`${notice} bg-[#DBEAFE] text-[#1D4ED8]`}>
              <span className="font-semibold">Security note:</span> Athios uses Stripe to connect
              your bank account. We never store your banking details.
            </p>

            <p className="text-xs text-muted-foreground">
              All payments are logged, FMV-compliant, and reported for end-of-year tax purposes.
              Payments are powered by Stripe.
            </p>

            {stripeStatus?.connected && stripeStatus.status === 'complete' ? (
              <p
                className={`${notice} flex items-center gap-2 bg-[#E8F9D2] font-semibold text-[#2F5E06]`}
              >
                <CheckCircle className="h-5 w-5" /> Your account is connected and ready for
                payouts.
              </p>
            ) : stripeStatus?.connected && stripeStatus.status === 'pending' ? (
              connectButton('Finish account setup')
            ) : (
              connectButton('Connect my bank account')
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

const PendingInvoicesPanel = ({ invoices }: { invoices: PendingInvoice[] }) => {
  const visible = invoices.filter(
    (inv) => inv.status === 'sent_to_athlete' || inv.status === 'sent_to_brand',
  )
  if (visible.length === 0) return null

  const formatCents = (cents: number, currency: string) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
      maximumFractionDigits: 0,
    }).format(cents / 100)

  return (
    <section>
      <h2 className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        <FileText className="h-3.5 w-3.5" /> Invoices · {visible.length}
      </h2>
      <div className="flex flex-col gap-2">
        {visible.map((inv) => {
          const pendingReview = inv.status === 'sent_to_athlete'
          return (
            <RouterLink
              key={inv.id}
              to={`/campaigns/${inv.collaboration_id}/invoice`}
              className={`group flex items-center gap-3 rounded-2xl border p-3 shadow-sm transition-colors ${
                pendingReview
                  ? 'border-[#C0FD72] bg-[#F4FDE9] hover:bg-[#E8F9D2]'
                  : 'border-border bg-card hover:bg-muted'
              }`}
            >
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                  pendingReview ? 'bg-[#DBEAFE] text-[#1D4ED8]' : 'bg-[#E8F9D2] text-[#2F5E06]'
                }`}
              >
                {pendingReview ? (
                  <Clock className="h-4 w-4" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-foreground">
                  {inv.invoice_number}
                </span>
                <span
                  className={`block text-sm ${pendingReview ? 'font-semibold text-[#3F7A0A]' : 'text-muted-foreground'}`}
                >
                  {pendingReview ? 'Your turn: review it' : 'Sent to the brand · awaiting payment'}
                </span>
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-foreground">
                {formatCents(inv.total_cents, inv.currency)}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </RouterLink>
          )
        })}
      </div>
    </section>
  )
}

export default Payments
